import React, { useEffect, useRef, useState } from "react";
import api, { persistAuthTokens } from "@/lib/api";

// OAuth client IDs are public identifiers. Keep the production ID as a safe
// fallback so a missing Pages build variable cannot silently remove sign-in.
const CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "1049101762294-do2o3lnuh1gl61rqb60r8c8cngpe7en0.apps.googleusercontent.com";
const SCRIPT_ID = "google-identity-services";

export default function GoogleSignIn({ onSignedIn, disabled = false }) {
  const button = useRef(null);
  const callback = useRef(null);
  const [error, setError] = useState("");

  callback.current = async ({ credential }) => {
    setError("");
    try {
      const response = await api.post("/auth/google", { credential });
      persistAuthTokens(response.data);
      await onSignedIn(response.data.user || (await api.get("/auth/me")).data);
    } catch (err) {
      setError(
        err.response?.status === 409
          ? "This email already has a password account. Sign in with your password."
          : "Google sign-in could not be completed. Please try again.",
      );
    }
  };

  useEffect(() => {
    if (!CLIENT_ID || disabled) return undefined;
    let active = true;
    const render = () => {
      if (!active || !button.current || !window.google?.accounts?.id) return;
      button.current.replaceChildren();
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: (value) => callback.current?.(value),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(button.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: Math.min(360, button.current.clientWidth || 360),
      });
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      if (window.google?.accounts?.id) render();
      else existing.addEventListener("load", render, { once: true });
    } else {
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = render;
      document.head.appendChild(script);
    }
    return () => {
      active = false;
      existing?.removeEventListener("load", render);
    };
  }, [disabled]);

  if (!CLIENT_ID) return null;
  return (
    <div className="google-auth">
      <div className="auth-divider">
        <span>or</span>
      </div>
      <div ref={button} aria-label="Continue with Google" />
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
